const IST_TZ = 'Asia/Kolkata';
const HAS_TZ = /[zZ]|[+-]\d{2}:?\d{2}$/;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const NAIVE_DT = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

const IST_DATE_OPTS = {
  timeZone: IST_TZ,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
};

const IST_DATETIME_OPTS = {
  ...IST_DATE_OPTS,
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

/** Local calendar date as YYYY-MM-DD (avoids UTC shift from toISOString). */
export const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** First and last calendar day of the current month (YYYY-MM-DD). */
export const getCurrentMonthRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const pad = (n) => String(n).padStart(2, '0');
  const lastDay = new Date(year, month + 1, 0).getDate();
  return {
    fromDate: `${year}-${pad(month + 1)}-01`,
    toDate: `${year}-${pad(month + 1)}-${pad(lastDay)}`,
  };
};

/** Parse API timestamps. Naive values are treated as IST, not UTC. */
export const parseIstDate = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  if (DATE_ONLY.test(raw)) {
    return new Date(`${raw}T00:00:00+05:30`);
  }
  const normalized = raw.replace(' ', 'T');
  if (NAIVE_DT.test(normalized) && !HAS_TZ.test(normalized)) {
    return new Date(`${normalized}+05:30`);
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Display date in IST (dd MMM yyyy). */
export const formatIndianDate = (value) => {
  const parsed = parseIstDate(value);
  if (!parsed) return '—';
  return parsed.toLocaleDateString('en-IN', IST_DATE_OPTS);
};

/** Display date + time in IST (dd/mm/yyyy, h:mm am/pm). */
export const formatIstDateTime = (value) => {
  const parsed = parseIstDate(value);
  if (!parsed) return '—';
  return parsed.toLocaleString('en-IN', IST_DATETIME_OPTS);
};

/** Display date + time in IST (dd MMM yyyy, h:mm am/pm). */
export const formatIndianDateTime = (value) => {
  const parsed = parseIstDate(value);
  if (!parsed) return '—';
  return parsed.toLocaleString('en-IN', {
    timeZone: IST_TZ,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/** Display time only in IST (h:mm am/pm). */
export const formatIndianTime = (value) => {
  const parsed = parseIstDate(value);
  if (!parsed) return '';
  return parsed.toLocaleTimeString('en-IN', {
    timeZone: IST_TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/** Short relative time: 2m ago, 3h ago, 5d ago. */
export const formatTimeAgo = (value) => {
  const parsed = parseIstDate(value);
  if (!parsed) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

/** Relative time in words: "17 min ago", "2 hours ago". */
export const formatRelativeTime = (value) => {
  const parsed = parseIstDate(value);
  if (!parsed) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes === 1 ? '1 min ago' : `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return days === 1 ? '1 day ago' : `${days} days ago`;
  return formatIndianDateTime(value);
};
